// The agent → Birtvy canvas bridge: turns the planning agent's Goal/Outline sections into Birtvy doc
// cards and maps the chat's planning model to a DeckGen generation model. The section-decode helpers are
// duplicated from canvas-old (they're route-local there; duplicate-don't-mutate).
import type { PlanningSection } from '~/types/overview'
import type { PlanningModel } from '~/hooks/usePlanningModel'
import type { GenerationModel } from '~/design-system/components/DeckGen/ai/deckGenSchema'
import { outlineToCanvasDocs } from '~/design-system/components/DeckGen/components/editor/canvasDocsUtils'
import type { EditCanvasDispatchEvent } from '~/utils/sse-parser'

/** Runaway `\\\\…` escapes from the old save loop — strip when detected (see canvas-old). */
function cleanRunawayEscapes(value: string | undefined): string | undefined {
  if (!value) return value
  return /\\{3,}/.test(value) ? value.replace(/\\+/g, '') : value
}

/** Some sections are stored JSON-string-encoded (leading `"`); decode back to clean markdown. */
function decodeQuotedSection(value: string | undefined): string | undefined {
  if (!value) return value
  const trimmed = value.trim()
  if (!trimmed.startsWith('"')) return value
  try {
    const parsed = JSON.parse(trimmed)
    if (typeof parsed === 'string') return parsed
  } catch {
    /* real newlines inside — strip wrapping quotes below */
  }
  return trimmed.replace(/^"+/, '').replace(/"+$/, '').replace(/\\"/g, '"').replace(/\\n/g, '\n')
}

/** Backend's PlanningSection.getEffectiveContent: a non-blank pendingContent supersedes content. */
export function effectiveSectionContent(
  section: { content?: string | null; pendingContent?: string | null } | undefined,
): string | undefined {
  const pending = section?.pendingContent
  const raw = pending && pending.trim() ? pending : section?.content
  return cleanRunawayEscapes(decodeQuotedSection(raw ?? undefined))
}

/** The agent doc cards Birtvy should show for the current sections: Goal first, then one per outline
 *  canvas. `key` is the stable seed identity ('goal' / 'canvas:<id>') — each key seeds at most once. */
export function desiredAgentDocs(sections: PlanningSection[] | undefined): Array<{ key: string; title: string; content: string }> {
  if (!sections?.length) return []
  const out: Array<{ key: string; title: string; content: string }> = []
  const goal = effectiveSectionContent(sections.find((s) => s.sectionType === 'GOAL'))
  if (goal && goal.trim()) out.push({ key: 'goal', title: 'Goal', content: goal })
  const outline = effectiveSectionContent(sections.find((s) => s.sectionType === 'OUTLINE'))
  for (const c of outlineToCanvasDocs(outline)) {
    if (c.content && c.content.trim()) out.push({ key: `canvas:${c.id}`, title: c.title, content: c.content })
  }
  return out
}

/** Same mapping the workplace/canvas-old use: chat planning model → closest DeckGen generation model. */
export const PLANNING_TO_DECKGEN_MODEL: Record<PlanningModel, GenerationModel> = {
  HAIKU_45: 'claude-sonnet-4-5',
  HAIKU_45_THINKING: 'claude-sonnet-4-5',
  SONNET_46: 'claude-sonnet-4-6',
  SONNET_46_THINKING: 'claude-sonnet-4-6',
  SONNET_5: 'claude-sonnet-5',
  OPUS_45: 'claude-sonnet-4-6',
  OPUS_45_THINKING: 'claude-sonnet-4-6',
  OPUS_46: 'claude-sonnet-4-6',
  OPUS_46_THINKING: 'claude-sonnet-4-6',
  OPUS_48: 'claude-opus-4-8',
  OPUS_48_THINKING: 'claude-opus-4-8',
  GEMINI_31_PRO_PREVIEW_THINKING: 'gemini-3.1-pro-preview',
  GEMINI_35_FLASH: 'gemini-3.1-pro-preview',
  GEMINI_35_FLASH_THINKING: 'gemini-3.1-pro-preview',
  // These map to THEMSELVES (unlike 3.5 Flash above, which predates its own generation entry): both are
  // registered generation models, so picking one in chat generates with the same model.
  GEMINI_38_FLASH: 'gemini-3.8-flash',
  GEMINI_38_FLASH_THINKING: 'gemini-3.8-flash',
  GEMINI_37_FLASH: 'gemini-3.7-flash',
  GEMINI_37_FLASH_THINKING: 'gemini-3.7-flash',
  GEMINI_36_FLASH: 'gemini-3.6-flash',
  GEMINI_36_FLASH_THINKING: 'gemini-3.6-flash',
  GEMINI_35_FLASH_LITE: 'gemini-3.5-flash-lite',
  DEEPSEEK_V41_FLASH: 'deepseek/deepseek-v4.1-flash',
  DEEPSEEK_V41_FLASH_THINKING: 'deepseek/deepseek-v4.1-flash',
  DEEPSEEK_V4_FLASH: 'deepseek/deepseek-v4-flash',
  DEEPSEEK_V4_FLASH_THINKING: 'deepseek/deepseek-v4-flash',
  DEEPSEEK_V4_PRO: 'deepseek/deepseek-v4-pro',
  DEEPSEEK_V4_PRO_THINKING: 'deepseek/deepseek-v4-pro',
  QWEN_PLUS: 'qwen/qwen3.7-plus',
  QWEN_MAX: 'qwen/qwen3.7-max',
  KIMI_K2: 'moonshotai/kimi-k2.6',
  KIMI_K3: 'moonshotai/kimi-k3',
  GPT_55: 'openai/gpt-5.5',
  GPT_56_SOL: 'openai/gpt-5.6-sol',
  GPT_56_TERRA: 'openai/gpt-5.6-terra',
  GPT_56_LUNA: 'openai/gpt-5.6-luna',
  GROK_46: 'x-ai/grok-4.6',
  GROK_45: 'x-ai/grok-4.5',
  // Newer chat models with no generation model of their own yet generate with the nearest one that has.
  OPUS_55: 'claude-opus-4-8',
  SONNET_55: 'claude-sonnet-5',
  OPUS_5: 'claude-opus-4-8',
  OPUS_47: 'claude-opus-4-7',
  GPT_61_SOL: 'openai/gpt-5.6-sol',
  GPT_6_SOL: 'openai/gpt-5.6-sol',
  GPT_6_LUNA: 'openai/gpt-5.6-luna',
  GROK_47: 'x-ai/grok-4.6',
  QWEN_38_MAX: 'qwen/qwen3.7-max',
  QWEN_38_FLASH: 'qwen/qwen3.7-plus',
}

/**
 * What THIS chat turn wants done with the images it referenced — the single place that reads the
 * editCanvasDispatch `imageIntent`.
 *
 * THE BUG THIS EXISTS FOR (seen in production): the user selected ten logos and asked to "generate a slide
 * and wire the logos inside it". Every chat-referenced image was wired as 'style', and a style wire feeds
 * generateSlide's vision attachments — pixels, no src. The model was then told to PLACE images it could
 * only see, so it invented filenames, and four elements baked into the saved slide with
 * url("http://localhost:5173/canvas/input_file_0.png") — a multimodal model's internal placeholder name.
 * Those 404 and render as empty boxes. 'place' wires the images as 'image' instead, which puts a real src
 * in the flow catalog and gives the model something it can actually reference.
 *
 * Only the exact string 'place' flips the mode. The backend always writes the key and sends null when it
 * has no opinion, so null, absent AND any value we don't recognise all fall through to the product's
 * long-standing default — style. This is live: an intent this build has never heard of must behave exactly
 * as it did before it existed.
 */
export const chatImageWireMode = (event: EditCanvasDispatchEvent): 'style' | 'image' =>
  event.imageIntent === 'place' ? 'image' : 'style'
